import clsx from 'clsx';
import { format, fromUnixTime, isBefore } from 'date-fns';
import { useState } from 'react';
import { useLocalization } from '@/hooks/use-localization';
import { DocumentItem as DocumentItemType, UrlVersion } from '@/queries/document';
import { Field } from '../forms/components/field';
import { Button } from '../typography/button/button';
import { DocumentLink } from './document-link';
import { HeadingHelper, LevelType } from '../heading-helper/heading-helper';

type DocumentItemProps = {
    title: string;
    items: DocumentItemType[];
    headingLevel: LevelType;
};

export const DocumentItem = ({ title, items, headingLevel }: DocumentItemProps) => {
    const { localize } = useLocalization();

    const [hasCheckedDocument, setHasCheckedDocument] = useState(false);
    const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
    const [dateInput, setDateInput] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [timeSensitiveDocument, setTimeSensitiveDocument] = useState<UrlVersion | null>(null);

    if (!items.length) {
        return null;
    }

    function findDocumentUrl(docs: UrlVersion[], date: string) {
        const targetDate = new Date(date);

        const sorted = [...docs].sort((a, b) => a.document_url_date - b.document_url_date);
        const validDocuments = sorted.filter(doc => !isBefore(targetDate, fromUnixTime(doc.document_url_date)));

        return validDocuments.length ? validDocuments[validDocuments.length - 1] : null;
    }

    const dateValidation = (date: string) => /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/.test(date);

    const handleNext = (urlVersions: UrlVersion[]) => {
        const isValid = dateValidation(dateInput);

        if (!isValid) {
            setTimeSensitiveDocument(null);
            return;
        }

        setHasCheckedDocument(true);
        setTimeSensitiveDocument(findDocumentUrl(urlVersions, dateInput));
    };

    const onSubmitExpanded = (item: DocumentItemType) => handleNext(item.url_versions);

    const renderTimeSensitiveDocumentLink = (item: DocumentItemType) => {
        if (timeSensitiveDocument?.document_url) {
            const documentTitle = `${item.name} (${format(fromUnixTime(timeSensitiveDocument.document_url_date), 'yyyy-MM-dd')})`;
            return <DocumentLink href={timeSensitiveDocument.document_url} title={documentTitle} parent={title} />;
        }

        if (hasCheckedDocument) {
            return <p>{localize('no-documents-for-date')}</p>;
        }

        return null;
    };

    return (
        <div data-component="document-item" className="r-w-full r-space-y-5">
            <HeadingHelper level={headingLevel} classList="r-text-xl r-font-medium">
                {title}
            </HeadingHelper>
            <ul className="r-flex r-w-full r-flex-col r-items-start r-space-y-0.5">
                {items.map((item: DocumentItemType) => {
                    const hasMultipleVersions = item.url_versions.length > 1;

                    return (
                        <li className="r-flex r-w-full r-flex-col" key={item.id}>
                            <div className="r-flex r-w-full r-items-start r-justify-between r-gap-2">
                                <DocumentLink href={item.url_versions[0].document_url} title={item.name} parent={title} />
                                {hasMultipleVersions ? (
                                    <button
                                        aria-label="Expand versions"
                                        aria-controls={`expanded-${item.id}`}
                                        aria-expanded={expandedItemId === item.id ? 'true' : 'false'}
                                        className="r-shrink-0 r-rounded-lg r-border r-border-gray-200 r-shadow-sm r-transition-colors hover:r-bg-gray-100"
                                        onClick={() => setExpandedItemId(prevId => (prevId === item.id ? null : item.id))}
                                    >
                                        <svg
                                            aria-hidden="true"
                                            fill="currentColor"
                                            height="16"
                                            width="16"
                                            viewBox="0 0 1792 1792"
                                            className={clsx('r-block r-size-8 r-p-1.5 r-transition-transform r-duration-100', {
                                                '-r-mb-0.5 r-mt-0.5 r-rotate-180': expandedItemId === item.id,
                                            })}
                                            style={{ display: 'inline-block', verticalAlign: 'middle' }}
                                        >
                                            <path d="M1683 808l-742 741q-19 19-45 19t-45-19l-742-741q-19-19-19-45.5t19-45.5l166-165q19-19 45-19t45 19l531 531 531-531q19-19 45-19t45 19l166 165q19 19 19 45.5t-19 45.5z"></path>
                                        </svg>
                                    </button>
                                ) : null}
                            </div>
                            {hasMultipleVersions && <p className="r-ml-7 r-text-xs">{localize('has-versions-text')}</p>}
                            <div
                                id={`expanded-${item.id}`}
                                aria-hidden={expandedItemId === item.id ? 'false' : 'true'}
                                className={clsx(
                                    'r-mb-3 r-mt-2 r-flex r-flex-col r-items-start r-space-y-2 r-rounded-lg r-border r-bg-gray-100 r-p-3',
                                    {
                                        'r-hidden': expandedItemId !== item.id,
                                    }
                                )}
                            >
                                <form
                                    className="r-space-y-5"
                                    onSubmit={e => {
                                        e.preventDefault();
                                        onSubmitExpanded(item);
                                    }}
                                >
                                    <p className="r-text-sm">{localize('has-versions-instruction')}</p>
                                    <Field
                                        validateOnEnter
                                        fieldName="Datum"
                                        value={dateInput}
                                        label={localize('date')}
                                        validation={dateValidation}
                                        errorMessage={localize('invalid-date')}
                                        onChange={e => setDateInput(e as string)}
                                        placeholder={localize('date-placeholder')}
                                    />

                                    <Button type="primary" size="small" isSubmit>
                                        {localize('generate')}
                                    </Button>
                                    {renderTimeSensitiveDocumentLink(item)}
                                </form>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};
